import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Thumbs, Zoom, Keyboard } from 'swiper/modules';
import endpoints from '../../define/endpoints';
import Button, { ButtonSizes, ButtonStyles } from '../Button/Button';
import ReviewMediaLabel from './ReviewMediaLabel';
import { TRANSLATIONS } from '../../../translations';
import CustomVideoPreview from '../FilterResults/components/CustomVideoPreview';
import CustomVideoComponent from '../CustomVideoComponent/CustomVideoComponent';
import { useMatchesMinWidth } from '../../hooks/useMatchesMinWidth';
import Typography, { TypographyStates, TypographyVariants } from '../../ui-components/Typography/Typography';
import BetaIcon, { IconColors, IconSizes } from '../Icons/Icon';
import { BREAKPOINTS } from '../../../constants';

const ImageShowcaseSwiper = ({
    open,
    setIsOpen,
    mediaOfficial,
    mediaCommunity,
    isDeviceSmallerThanMblLandscape,
    communityCounter,
    officialCounter,
    className,
    initialIndex = 0
}) => {
    const isDeviceSmallerThanMin = !useMatchesMinWidth(375);
    const isDeviceSmallerThanTablet = !useMatchesMinWidth(BREAKPOINTS.TABLET);

    const [activeTab, setActiveTab] = useState('official');
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [mainSwiper, setMainSwiper] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0)
    const [videoIsPlaying, setVideoIsPlaying] = useState(false);
    const [isZoomed, setIsZoomed] = useState(false);
    const mediaOfficialCheck = mediaOfficial || [];
    const mediaCommunityCheck = mediaCommunity || [];

    const VIDEO = 'video';
    const OFFICIAL = 'official';
    const COMMUNITY = 'community';

    if (!open) return null;

    const allSlides = [
        ...mediaOfficialCheck.map(item => ({ ...item, mediaType: OFFICIAL })),
        ...mediaCommunityCheck.map(item => ({ ...item, mediaType: COMMUNITY }))
    ];
    const communityStartIndex = mediaOfficial ? mediaOfficial.length : 0;
    const goToOfficial = () => {
        setActiveTab(OFFICIAL);
        mainSwiper?.slideTo(0);
        thumbsSwiper?.slideTo(0);
    };
    const goToCommunity = () => {
        setActiveTab(COMMUNITY);
        mainSwiper?.slideTo(communityStartIndex);
        thumbsSwiper?.slideTo(communityStartIndex);
    };
    const navigationButtons = (
        <div className={`swiper-navigation-buttons`}>
            <span name={'arrow-left'} className={`${className} swiper-button-prev`}></span>
            <span name={'arrow-right'} className={`${className} swiper-button-next`}></span>
        </div>
    );

    const close = () => {
        if (isDeviceSmallerThanTablet) return;
        setIsOpen(false)
    }

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setIsOpen(false);
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    return (
        <>
            <div className="image-showcase-swiper__header" onClick={() => close()}>
                <div className='image-showcase-swiper__header__close-button'>
                    <Button
                        onClick={() => setIsOpen(false)}
                        style={ButtonStyles.text}
                        size={ButtonSizes.xxs}
                        iconRight={
                            <BetaIcon
                                name={'close'}
                                color={IconColors.ghost}
                                size={IconSizes[16]}
                            />
                        }
                    />
                </div>
                <div className='image-showcase-swiper__header__tabs'>
                    {mediaCommunity && mediaOfficial && (
                        <div onClick={(e) => e.stopPropagation()}>
                            <Button
                                style={activeTab === OFFICIAL ? ButtonStyles.textInvertUnderlinedAlways : ButtonStyles.textInvert}
                                label={<Typography
                                    variant={isDeviceSmallerThanMin ? TypographyVariants.p3 : TypographyVariants.p2}
                                    state={TypographyStates.inverted}
                                >
                                    {`${TRANSLATIONS.OFFICIAL_MEDIA} (${officialCounter})`}
                                </Typography>}
                                onClick={() => goToOfficial()}
                            />

                            <Button
                                style={activeTab === COMMUNITY ? ButtonStyles.textInvertUnderlinedAlways : ButtonStyles.textInvert}
                                label={<Typography
                                    variant={isDeviceSmallerThanMin ? TypographyVariants.p3 : TypographyVariants.p2}
                                    state={TypographyStates.inverted}
                                >
                                    {`${TRANSLATIONS.COMMUNITY_MEDIA} (${communityCounter})`}
                                </Typography>}
                                onClick={() => goToCommunity()}
                            />
                        </div>
                    )}
                </div>
            </div >

            <div>
                {!isDeviceSmallerThanMblLandscape && navigationButtons}

                <Swiper
                    initialSlide={initialIndex}
                    className={className}
                    slidesPerView={1}
                    modules={[Navigation, Thumbs, Zoom, Keyboard]}
                    navigation={{
                        nextEl: ".swiper-button-next",
                        prevEl: ".swiper-button-prev",
                    }}
                    keyboard={{
                        enabled: true,
                        onlyInViewport: true,
                    }}
                    spaceBetween={0}
                    zoom={isDeviceSmallerThanTablet}
                    onZoomChange={(swiper, scale) => {
                        setIsZoomed(scale > 1);
                    }}
                    centeredSlides={true}
                    thumbs={{ swiper: thumbsSwiper }}
                    onSwiper={setMainSwiper}
                    onSlideChange={(swiper) => {
                        const currentIndex = swiper.activeIndex;
                        setVideoIsPlaying(false);
                        setActiveIndex(currentIndex);

                        if (thumbsSwiper && thumbsSwiper.slideToLoop) {
                            thumbsSwiper.slideToLoop(currentIndex);
                        }
                        if (currentIndex < communityStartIndex) {
                            setActiveTab(OFFICIAL);
                        } else {
                            setActiveTab(COMMUNITY);
                        }
                    }}
                >
                    {allSlides.map((item, index) => {
                        const isVideo = item.type === VIDEO;
                        return (
                            <SwiperSlide
                                key={index}
                            >
                                <div className='image-showcase-swiper__slide-wrapper' onClick={() => close()}>
                                    <div className={`image-showcase-swiper__slide-wrapper--frame${isZoomed ? 'full-h' : ''}`} onClick={(e) => e.stopPropagation()}>
                                        {item.mediaType === COMMUNITY && !videoIsPlaying && !isZoomed && (
                                            <ReviewMediaLabel
                                                score={item.rating}
                                                firstName={item.first_name}
                                                lastName={item.last_name}
                                                profileImage={item.profile_image}
                                                date={item.date}
                                            />
                                        )}

                                        {isVideo ? (
                                            <CustomVideoComponent
                                                src={`${endpoints.bucket}${item.src}`}
                                                alt={item.alt || ''}
                                                mutedByDefault={false}
                                                activeIndex={activeIndex}
                                                videoIsPlaying={index === activeIndex}
                                                setVideoIsPlaying={setVideoIsPlaying}
                                            />
                                        ) : (
                                            <div className="swiper-zoom-container">
                                                <img
                                                    src={`${endpoints.bucket}${item.src}`}
                                                    alt={item.alt || ''}
                                                    loading='lazy'
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            </div >

            {/* thumbnails */}
            < div className="image-showcase-swiper__thumbnail-wrapper" onClick={() => close()}>
                <div style={{ maxWidth: '80vw', width: 'fit-content' }} onClick={(e) => e.stopPropagation()}>
                    <Swiper
                        slidesPerView={'auto'}
                        modules={[Thumbs]}
                        onSwiper={setThumbsSwiper}
                        watchSlidesProgress
                        spaceBetween={16}
                    >
                        {allSlides.map((item, index) => {
                            const isVideo = item.type === 'video';

                            return (
                                <SwiperSlide
                                    key={index}
                                    onClick={() => mainSwiper?.slideTo(index)}
                                >
                                    {isVideo ? (
                                        <CustomVideoPreview
                                            imageSrc={`${item.original_src}`}
                                            alt={item.alt}
                                            cndString="?crop=1:1,smart&width=160"
                                        />
                                    ) : (
                                        <img
                                            src={`${endpoints.bucket}${item.original_src}?crop=1:1,smart&width=160`}
                                            alt={item.alt || ''}
                                            loading='lazy'
                                        />
                                    )}
                                </SwiperSlide>
                            )
                        })}
                    </Swiper>
                </div>
            </div>
        </>
    )
};


ImageShowcaseSwiper.propTypes = {
    open: PropTypes.bool.isRequired,
    mediaOfficial: PropTypes.arrayOf(
        PropTypes.shape({
            src: PropTypes.string.isRequired,
            alt: PropTypes.string,
            type: PropTypes.oneOf(['image', 'video']).isRequired,
        })
    ),
    mediaCommunity: PropTypes.arrayOf(
        PropTypes.shape({
            src: PropTypes.string.isRequired,
            alt: PropTypes.string,
            type: PropTypes.oneOf(['image', 'video']).isRequired,
            date: PropTypes.string,
            first_name: PropTypes.string,
            last_name: PropTypes.string,
            profile_image: PropTypes.string,
            score: PropTypes.number,
        })
    ),
    isDeviceSmallerThanMblLandscape: PropTypes.bool.isRequired,
    communityCounter: PropTypes.number,
    officialCounter: PropTypes.number,
};

export default ImageShowcaseSwiper